import { Injectable } from '@angular/core';
import { uuid } from '@antv/x6/lib/util/string/uuid';
import { Subject } from 'rxjs';
import { defaultTitles } from './toast-constants';
import {
  AdditionalToastData,
  ToastButton,
  ToastLink,
  ToastData,
} from './toast-interfaces';

@Injectable({
  providedIn: 'root',
})
export class CustomToastService {
  SUCCESS_TOAST_DEFAULT_DURATION = 10000;
  constructor() {}

  defaultTitles = defaultTitles;
  toastList: ToastData[] = [];
  public open = new Subject<ToastData[]>();

  /**
   * @deprecated
   */
  private buttonClickSubject: Subject<ToastData> = new Subject<ToastData>();
  buttonClick$ = this.buttonClickSubject.asObservable();

  // Create a subject for handling toast action clicks
  private toastActionClickSubject: Subject<ToastLink | ToastButton> =
    new Subject<ToastLink | ToastButton>();
  toastActionClick$ = this.toastActionClickSubject.asObservable();

  /**
   * Shows a new toast with the provided data.
   * @param data The data for the toast, including content, type, and other configuration.
   */
  show(toast: ToastData) {
    if (!toast?.duration && toast?.status === 'success') {
      toast.duration = this.SUCCESS_TOAST_DEFAULT_DURATION;
    }
    let newToast = { ...toast, show: true, id: uuid() };
    this.toastList.unshift(newToast); // Use unshift to add the newest toaster at the beginning
    this.open.next(this.toastList);
  }

  /**
   * Hides and removes a toast from the list based on its index.
   * @param toastIndex The index of the toast to be hidden and removed.
   */
  hide(toastIndex: number) {
    this.toastList[toastIndex].show = false;
    this.toastList.splice(toastIndex, 1);
    this.open.next(this.toastList);
  }

  /**
   * Displays a toast based on the provided status and content or AdditionalToastData.
   * @param status - Status of the toast (success, error, warning, info).
   * @param arg - Either a string containing the content or an AdditionalToastData object.
   *              If a string, it represents the content to be shown in the toast.
   *              If an object (AdditionalToastData), it can override default title and duration or it can include properties like
   *              link, button, icon, etc., to customize the appearance of the toast.
   *              The status will be determined by the function called (e.g., success, warning).
   */
  private showToastBasedOnStatus(
    status: 'success' | 'error' | 'warning' | 'info',
    arg: string | AdditionalToastData,
  ): void {
    let additionalToastData: AdditionalToastData =
      typeof arg === 'string' ? { content: arg } : arg;
    const defaultDuration =
      status === 'success' ? this.SUCCESS_TOAST_DEFAULT_DURATION : 0;

    const data: ToastData = {
      ...additionalToastData,
      title: additionalToastData?.title || this.defaultTitles[status],
      status,
      duration:
        additionalToastData?.duration >= 0
          ? additionalToastData?.duration
          : defaultDuration,
    };

    this.show(data);
  }

  /**
   * Displays a success toast with the provided content or AdditionalToastData.
   * @param arg - Either a string containing the content or an AdditionalToastData object.
   */
  success(arg: string | AdditionalToastData): void {
    this.showToastBasedOnStatus('success', arg);
  }

  /**
   * Displays an error toast with the provided content or AdditionalToastData.
   * @param arg - Either a string containing the content or an AdditionalToastData object.
   */
  error(arg: string | AdditionalToastData): void {
    this.showToastBasedOnStatus('error', arg);
  }

  /**
   * Displays a warning toast with the provided content or AdditionalToastData.
   * @param arg - Either a string containing the content or an AdditionalToastData object.
   */
  warning(arg: string | AdditionalToastData): void {
    this.showToastBasedOnStatus('warning', arg);
  }

  /**
   * Displays an info toast with the provided content or AdditionalToastData.
   * @param arg - Either a string containing the content or an AdditionalToastData object.
   */
  info(arg: string | AdditionalToastData): void {
    this.showToastBasedOnStatus('info', arg);
  }

  /** @deprecated
   * Emit a signal when the button is clicked
   */
  onButtonClick(buttonObj) {
    this.buttonClickSubject.next(buttonObj);
  }

  /**
   * Notifies observers when a toast action (button or link) is clicked.
   * @param item - The toast item (button or link) that was clicked.
   */
  notifyToastActionClick(item: ToastButton | ToastLink): void {
    this.toastActionClickSubject.next(item);
  }
}
